/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import { Dialog, Typography, Button, Checkbox } from '@pwskills/rachnaui';
import CloseIcon from 'public/images/codingProject/CloseIcon';
import { IcertificateDetails } from '../../TypesAndInterfaces';
import {
  sendCertificateShareInteractedEvent,
  sendChooseCertificateOptionToggledEvent,
} from '../utils/certificateGAEvents';

interface CertificateSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  allCertificates: IcertificateDetails[];
  selectedIndex: number;
  title: string;
  description: string;
  submitButtonText: string;
  onSubmit: (selectedCertificates: IcertificateDetails[], selectedIndices: number[]) => void;
  isLoading?: boolean;
  showSelectAll?: boolean;
  sharedMedium?: string;
}

const CertificateSelectionModal: React.FC<CertificateSelectionModalProps> = ({
  isOpen,
  onClose,
  allCertificates,
  selectedIndex,
  title,
  description,
  submitButtonText,
  onSubmit,
  isLoading = false,
  showSelectAll = false,
  sharedMedium = '',
}) => {
  const [selectedCerts, setSelectedCerts] = useState<Record<number, boolean>>({});

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      const initialSelection: Record<number, boolean> = {};
      allCertificates.forEach((_, index) => {
        initialSelection[index] = index === selectedIndex;
      });
      setSelectedCerts(initialSelection);
    }
  }, [isOpen, selectedIndex, allCertificates]);
  useEffect(() => {
    if (sharedMedium && isOpen && allCertificates.length > 0) {
      const partnerCertCount = allCertificates.filter((c) => c.isPartnerCertificate).length;
      const currentCert = allCertificates[selectedIndex];
      const certificateIds = allCertificates.map((c) => c.certificate?.id).filter(Boolean);
      sendCertificateShareInteractedEvent({
        sharing_medium: sharedMedium,
        certificate_shared_count: certificateIds.length,
        certificate_included: certificateIds,
        course_name: currentCert?.courseName,
        course_id: currentCert?.certificate?._id,
        partner_certs_available_count: partnerCertCount,
      });
    }
  }, [sharedMedium, isOpen, allCertificates, selectedIndex]);
  const handleCheckboxChange = (index: number) => {
    const cert = allCertificates[index];
    const newValue = !selectedCerts[index];
    const partnerCertCount = allCertificates.filter((c) => c.isPartnerCertificate).length;

    // Track toggle event
    sendChooseCertificateOptionToggledEvent({
      certificate_id: cert.certificate?.id,
      certificate_index: index,
      is_selected: newValue,
      certificate_type: cert.certificate?.type || cert.certificateType,
      course_name: cert.courseName,
      course_id: cert.certificate?._id,
      partner_certs_available_count: partnerCertCount,
      is_partner_certificate: cert.isPartnerCertificate,
      option_name: cert.partnerName,
    });

    setSelectedCerts((prev) => ({
      ...prev,
      [index]: newValue,
    }));
  };

  const handleSelectAll = () => {
    const allSelected = allCertificates.every((_, index) => selectedCerts[index]);
    const newValue = !allSelected;
    const newSelection: Record<number, boolean> = {};
    const partnerCertCount = allCertificates.filter((c) => c.isPartnerCertificate).length;

    allCertificates.forEach((_, index) => {
      newSelection[index] = newValue;
    });

    // Track toggle event once with all certificate IDs
    const allCertificateIds = allCertificates
      .map((cert) => cert.certificate?.id)
      .filter(Boolean)
      .join(',');
    const allCertificateTypes = allCertificates
      .map((cert) => cert.certificate?.type || cert.certificateType)
      .filter(Boolean)
      .join(',');
    const allPartnerNames = allCertificates
      .map((cert) => cert.partnerName)
      .filter(Boolean)
      .join(',');

    sendChooseCertificateOptionToggledEvent({
      certificate_id: allCertificateIds,
      certificate_index: 0,
      is_selected: newValue,
      certificate_type: allCertificateTypes,
      course_name: allCertificates[0]?.courseName,
      course_id: allCertificates[0]?.certificate?._id,
      partner_certs_available_count: partnerCertCount,
      is_partner_certificate: allCertificates.some((cert) => cert.isPartnerCertificate),
      partner_name: allPartnerNames,
      option_name: 'All',
    });

    setSelectedCerts(newSelection);
  };

  const getSelectedCount = () => {
    return Object.values(selectedCerts).filter(Boolean).length;
  };

  const isAllSelected =
    allCertificates.length > 0 && allCertificates.every((_, index) => selectedCerts[index]);

  const handleSubmit = () => {
    const selectedIndices = Object.entries(selectedCerts)
      .filter(([, isSelected]) => isSelected)
      .map(([index]) => parseInt(index, 10));

    const selectedCertificates = allCertificates.filter((_, index) => selectedCerts[index]);

    if (selectedCertificates.length === 0) {
      return;
    }

    onSubmit(selectedCertificates, selectedIndices);
  };

  // Don't render if only one certificate
  if (allCertificates.length <= 1) {
    return null;
  }

  const selectedCount = getSelectedCount();

  return (
    <Dialog
      open={isOpen}
      closeModal={onClose}
      title=""
      isOutSideClick
      titleClass="!p-0"
      titleWrapperClass="!border-b-0 !pb-0"
      className="!rounded-xl overflow-hidden gap-4 flex flex-col !p-0"
      closeEnable={false}
    >
      <div className="flex flex-col gap-2 !p-5 w-full">
        <div className="flex w-full items-center justify-between">
          <Typography
            component="body-regular"
            variant="bold"
            className="text-gray-850"
            style={{ fontSize: '20px', lineHeight: '28px' }}
          >
            {title}
          </Typography>
          <button type="button" onClick={onClose} aria-label="Close">
            <CloseIcon width="20" />
          </button>
        </div>
        <Typography
          component="body-regular"
          variant="regular"
          className="text-gray-850"
          style={{
            fontFamily: 'Nunito Sans',
            fontSize: '18px',
            lineHeight: '26px',
            fontWeight: 400,
          }}
        >
          {description}
        </Typography>

        <div className="flex flex-wrap gap-2 h-full">
          {/* Select All checkbox */}
          {showSelectAll && (
            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
              <Checkbox
                checked={isAllSelected}
                onChange={handleSelectAll}
                id="cert-modal-checkbox-all"
              />
              <label htmlFor="cert-modal-checkbox-all" className="cursor-pointer flex-1">
                <Typography component="body-small" variant="regular" className="text-gray-800">
                  All
                </Typography>
              </label>
            </div>
          )}

          {allCertificates.map((cert, index) => (
            <div
              key={`cert-modal-${cert.certificate?.id || index}`}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 max-w-full overflow-x-auto"
            >
              <Checkbox
                checked={selectedCerts[index] || false}
                onChange={() => handleCheckboxChange(index)}
                id={`cert-modal-checkbox-${index}`}
              />
              <label htmlFor={`cert-modal-checkbox-${index}`} className="cursor-pointer flex-1">
                <Typography component="body-small" variant="regular" className="text-gray-800">
                  {cert.partnerName ? `${cert.partnerName}` : ''}
                </Typography>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="flex gap-2 justify-end h-[64px] px-[15px] py-3 rounded-b-xl bg-[#FAF9F6]">
        <Button
          onClick={handleSubmit}
          disabled={selectedCount === 0 || isLoading}
          className="!px-6 !py-2"
        >
          {isLoading ? 'Processing...' : `${submitButtonText}`}
        </Button>
      </div>
    </Dialog>
  );
};

export default CertificateSelectionModal;
